import React from 'react';

import { useQueryClient } from 'react-query';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useVersionStore } from 'store/version.store';

import { useMutationChangeAssignee } from 'api/mutations/changeAssignee.mutation';
import { cardsDataQueryKey } from 'api/queries/cardsData.query';

import { AssigneeAvatarWithSelection } from 'components/common/AssigneeAvatarWithSelection/AssigneeAvatarWithSelection';
import { CustomizedStatusSelectWrapper } from 'components/common/CustomizedStatusSelect/CustomizedStatusSelectWrapper/CustomizedStatusSelectWrapper';
import { TrackedUntrackedAction } from 'components/common/TrackedUntrackedAction/trackedUntrackedAction';

import {
    IAssignee,
    ICardType,
    ICategory,
    IStatus,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/battlecards.types';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/customCard/customCard.module.scss';
import { colorByCardType } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { CustomizedTooltip } from 'components/common';

interface Props {
    id: string;
    type: ICardType;
    category: ICategory;
    taskActivityType: string;
    assignee: IAssignee;
    title: string;
    parentActivity?: string;
    showStatus: boolean;
    taskActivityId: string;
    isTracked: boolean;
    taskVersionHashCode: string;
    isVirtual: boolean;
    originalStatus: IStatus;
    status: IStatus;
    taskId: number;
    queryKeys: string[];
    isInterimChanged?: boolean;
}

export const CustomCardHeader = ({
    id,
    type,
    category,
    taskActivityType,
    assignee,
    title,
    parentActivity,
    showStatus,
    taskActivityId,
    isTracked,
    taskVersionHashCode,
    isVirtual,
    originalStatus,
    status,
    taskId,
    queryKeys,
    isInterimChanged,
}: Props) => {
    const isProgramActivity = type?.id === 2;
    const _id = isProgramActivity ? taskActivityId : `C-${id.split('_')[0]}`;

    const currentVersion = useVersionStore((state) => state.version);
    const { mutate } = useMutationChangeAssignee();
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();

    const handleAssigneeSelection = (value) => {
        if (currentVersion) {
            mutate(
                {
                    cardId: taskId,
                    isVirtual: isVirtual,
                    username: value,
                    versionId: currentVersion.id,
                },
                {
                    onSuccess: () => {
                        handleSnackBar('Assignee Successfully Changed', 'success');
                        return Promise.all(['relatedCards'].map((key) => queryClient.invalidateQueries(key)));
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    return (
        <div data-testid={'custom-card-header'} className={classes.header}>
            <div className={classes.headerTop}>
                <div className={classes.headerTopInside}>
                    <CustomizedTooltip
                        tooltipContent={_id}
                        triggerElement={<div className={classes.headerTopInsideId}>{_id}</div>}
                    />
                    <div>|</div>
                    <div
                        data-testid={'inside-label'}
                        className={classes.headerTopInsideLabel}
                        style={{ color: colorByCardType(type?.name) }}
                    >
                        {type?.label}
                    </div>
                    <div>|</div>
                    <div className={classes.headerTopInsideActivity}>
                        {isProgramActivity ? taskActivityType : category?.label}
                    </div>
                </div>

                <div data-testid={'status-assignee'} className={classes.statusAssigneeContainer}>
                    {isProgramActivity && (
                        <div className={`${classes.trackedUntrackedWrapper} ${isTracked ? classes.tracked : ''}`}>
                            <TrackedUntrackedAction
                                isTracked={isTracked}
                                taskVersionHashCode={taskVersionHashCode}
                                invalidateQueriesKey={[cardsDataQueryKey, 'CountersData', ...queryKeys]}
                            />
                        </div>
                    )}

                    {showStatus && (
                        <div className={classes.activityStatusWrapper}>
                            <CustomizedStatusSelectWrapper
                                category={category}
                                isProgramActivity={isProgramActivity}
                                status={status}
                                taskId={taskId}
                                isVirtual={isVirtual}
                                invalidateQueriesKey={['useQueryPrioritiesMatrixCards', ...queryKeys]}
                                originalStatus={originalStatus}
                                isInterimChanged={isInterimChanged}
                            />
                        </div>
                    )}

                    {assignee && (
                        <AssigneeAvatarWithSelection assignee={assignee} handleSelection={handleAssigneeSelection} />
                    )}
                </div>
            </div>

            <CustomizedTooltip
                tooltipContent={title}
                triggerElement={<div className={classes.headerTitle}>{title}</div>}
            />
            <div className={classes.headerActivity}>{parentActivity}</div>
        </div>
    );
};
